/**
 * Buffered, retry-tolerant analytics emitter.
 *
 * Why batch + flush rather than POST per emit:
 *   - The hot path (a song running) emits multiple events in quick
 *     succession; coalescing keeps the network noise low.
 *   - Beacon-on-unload only works if there's a payload ready to send —
 *     buffering lets us catch in-flight events on the same beacon.
 *
 * Why `fetch(..., { keepalive: true })` instead of `navigator.sendBeacon`:
 *   - `sendBeacon` can't carry custom headers, so we'd lose the
 *     `Authorization: Bearer ...` token and the server would 401 the
 *     beacon. `keepalive: true` is the modern equivalent that survives
 *     unload AND carries headers — supported by every browser since
 *     Chrome 66 / Safari 13 / Firefox 121.
 *   - Lets us reuse the same `authFetchJson` flow with retries and
 *     consistent error handling for the periodic-flush path.
 *
 * Why `/api/sessions/events` and not `/api/analytics/events`:
 *   - uBlock Origin / EasyPrivacy / AdGuard / Brave Shields all match
 *     URLs containing `analytics`, silently dropping the POST at the
 *     browser layer. The flush would catch a TypeError, re-queue, and
 *     eventually evict the events past `MAX_BUFFER_SIZE`. Don't put
 *     `analytics`, `track`, `telemetry`, `pixel`, or `metrics` back in
 *     the URL.
 */

import { authFetch } from '../api';
import {
  ANALYTICS_MAX_BATCH_SIZE,
  type AnalyticsEventDims,
  type AnalyticsEventPayload,
  type AnalyticsEventType,
} from '@shared/analytics';

export interface EmitInput extends AnalyticsEventDims {
  eventType: AnalyticsEventType;
  /** Anything not promoted to a column. Stays JSON-primitive only. */
  props?: Record<string, string | number | boolean | null>;
}

/** Adjustable so tests can run with tight intervals; prod uses the defaults. */
export interface AnalyticsClientOptions {
  /** Flush when this many events are buffered. */
  flushBatchSize?: number;
  /** Flush at least this often (ms) even if the buffer isn't full. */
  flushIntervalMs?: number;
}

const DEFAULT_FLUSH_BATCH_SIZE = 10;
const DEFAULT_FLUSH_INTERVAL_MS = 5_000;
/**
 * Hard cap on the in-memory buffer so a runaway emitter (or a
 * persistently failing server) can't pin unbounded memory in the
 * browser tab. Anything beyond this drops the oldest events first
 * with a console warning.
 */
const MAX_BUFFER_SIZE = 500;

export class AnalyticsClient {
  private queue: AnalyticsEventPayload[] = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private flushBatchSize: number;
  private flushIntervalMs: number;
  private disposed = false;

  constructor(opts: AnalyticsClientOptions = {}) {
    this.flushBatchSize = opts.flushBatchSize ?? DEFAULT_FLUSH_BATCH_SIZE;
    this.flushIntervalMs = opts.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;
  }

  /** Push an event onto the queue. Triggers a flush if the batch is full. */
  emit(input: EmitInput): void {
    if (this.disposed) {
      return;
    }
    const payload: AnalyticsEventPayload = {
      eventId: generateEventId(),
      eventType: input.eventType,
      occurredAt: new Date().toISOString(),
      mode: input.mode,
      mapId: input.mapId,
      mapSlug: input.mapSlug,
      difficulty: input.difficulty,
      variant: input.variant,
      progressTier: input.progressTier,
      completed: input.completed,
      durationMs: input.durationMs,
      props: input.props,
    };
    this.queue.push(payload);
    if (this.queue.length > MAX_BUFFER_SIZE) {
      // Drop oldest. We prefer keeping recent events because they're
      // more likely to carry actionable signal (a fresh death) than
      // hours-old emits.
      const dropped = this.queue.length - MAX_BUFFER_SIZE;
      this.queue.splice(0, dropped);
      console.warn(`Analytics buffer overflow: dropped ${dropped} oldest events`);
    }
    if (this.queue.length >= this.flushBatchSize) {
      void this.flush('size');
    } else {
      this.scheduleTimerFlush();
    }
  }

  /**
   * Flush whatever's queued. `reason` lets the unload-path opt into
   * `keepalive: true` semantics; the periodic flush uses normal fetch.
   */
  async flush(reason: 'size' | 'timer' | 'unload' | 'manual' = 'manual'): Promise<void> {
    if (this.queue.length === 0) {
      return;
    }
    const batch = this.queue.splice(0, this.flushBatchSize);
    this.cancelTimer();
    const keepalive = reason === 'unload';
    try {
      const res = await authFetch('/api/sessions/events', {
        method: 'POST',
        json: { events: batch },
        keepalive,
      });
      if (!res.ok) {
        // 4xx means the events themselves were rejected — re-queueing
        // would just fail forever. Log the body so dropped batches are
        // diagnosable from the browser console.
        if (res.status >= 400 && res.status < 500) {
          let errorCode: string | undefined;
          try {
            const body = (await res.json()) as { error?: string };
            errorCode = body?.error;
          } catch {
            // Body might not be JSON — that's fine, we still warn.
          }
          console.warn(
            `Analytics ingest rejected ${batch.length} events: ${res.status}${
              errorCode ? ` (${errorCode})` : ''
            }`,
          );
          return;
        }
        // 5xx: transient. Put them back at the front of the queue so
        // the next flush gets them in original order. Don't retry
        // synchronously — back off until the next periodic flush.
        this.queue.unshift(...batch);
        return;
      }
    } catch (err) {
      // Network failure. Same logic as 5xx.
      this.queue.unshift(...batch);
      console.warn(`Analytics flush failed (${reason}):`, err);
    }
  }

  /** Stop scheduling. Idempotent. Future emits become no-ops. */
  dispose(): void {
    this.cancelTimer();
    this.disposed = true;
  }

  /**
   * Drain everything in the queue. The unload path fans the queue into
   * parallel `keepalive: true` fetches — a sequential `await` loop
   * would only complete the first batch before the JS context dies on
   * page-hide, dropping every event past `flushBatchSize`. The manual
   * / timer paths still go sequential so failures back-pressure the
   * caller naturally.
   */
  async flushAll(reason: 'size' | 'timer' | 'unload' | 'manual' = 'manual'): Promise<void> {
    if (this.queue.length === 0) {
      return;
    }
    if (reason === 'unload') {
      const inFlight: Promise<unknown>[] = [];
      while (this.queue.length > 0) {
        const batch = this.queue.splice(0, ANALYTICS_MAX_BATCH_SIZE);
        inFlight.push(
          authFetch('/api/sessions/events', {
            method: 'POST',
            json: { events: batch },
            keepalive: true,
          }).catch((err) => {
            console.warn('Analytics unload flush failed:', err);
          }),
        );
      }
      this.cancelTimer();
      await Promise.allSettled(inFlight);
      return;
    }
    while (this.queue.length > 0) {
      const before = this.queue.length;
      await this.flush(reason);
      if (this.queue.length >= before) {
        return;
      }
    }
  }

  /** Test-only: peek the in-memory queue. */
  _peekQueue(): ReadonlyArray<AnalyticsEventPayload> {
    return this.queue;
  }

  private scheduleTimerFlush(): void {
    if (this.timer !== null) {
      return;
    }
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.flush('timer');
    }, this.flushIntervalMs);
  }

  private cancelTimer(): void {
    if (this.timer !== null) {
      clearTimeout(this.timer);
      this.timer = null;
    }
  }
}

/**
 * UUID v4 generation. `crypto.randomUUID` is on every browser we
 * support; the manual fallback below is here as a final belt-and-braces
 * for unusual sandboxes (older Safari iframes, content scripts running
 * before crypto is exposed).
 */
function generateEventId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i += 1) {
      bytes[i] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0'));
  return (
    `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-` +
    `${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`
  );
}

/**
 * Singleton instance used by the React provider. Tests instantiate
 * their own {@link AnalyticsClient} directly; we don't expose the
 * singleton for mutation outside this module.
 */
let _singleton: AnalyticsClient | null = null;

export function getAnalyticsClient(): AnalyticsClient {
  if (!_singleton) {
    _singleton = new AnalyticsClient();
  }
  return _singleton;
}

/** Test-only: replace the singleton (for setup/teardown around tests). */
export function _replaceAnalyticsClientForTests(next: AnalyticsClient | null): void {
  if (_singleton) {
    _singleton.dispose();
  }
  _singleton = next;
}
